import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ExternalLink, X, User, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import PatronComplianceStrip from '../../components/patron/PatronComplianceStrip'
import { formatGamingMoney, formatMeterCents } from '../../utils/gamingCurrency'
import { formatSydneyDateTime } from '../../utils/sydneyDateTime'
import {
  ALERT_GAMING_CONTEXT_HALF_MS,
  alertWindowBounds,
  buildGamingDataUrl,
  buildCumulativeMeterComparisonTable,
  displayTriggerFieldKey,
  extractCumulativeMeterSnapshots,
  findTriggerCombinedRowIndex,
  findTriggerMeterRowIndex,
  gamingModalCombinedFallbackRows,
  gamingModalMeterFallbackRows,
  gamingModalSessionsFallbackRows,
  getAlertRowLinkageRows,
  getDetailMetaRows,
  getFieldTooltip,
  getMeterModalEvaluatedContext,
  getTriggerEvidenceRows,
  groupTriggeredDataFields,
  parseDetailsObject,
  resolveAlertGamingContextAnchor,
  resolveAlertMemberIdForMeterModal,
  resolveAlertSessionPk,
  buildWhyThisFired,
  splitCombinedTriggerRows,
} from './alertContextUtils'
import {
  formatProcessingCategoryLabel,
  formatRiskTierLabel,
  formatRuleTypeUi,
  formatSeverityLabel,
  formatTagLines,
  buildAustracTagTooltip,
  processingCategoryExplanation,
  riskTierStyle,
  ruleHasAustracSmrTags,
} from './alertDetailVocabulary'

const C = {
  pri: '#0a5169',
  slate: '#64748b',
  slateL: '#94a3b8',
  slateBg: '#f8fafc',
  red: '#dc2626',
}

const T = { small: 12, normal: 14 }

// SVCP §5.2 — structured closure reasons. Must match the six values in
// migration 137 and the ALLOWED_REASONS set in backend/src/routes/alerts.js.
const CLOSURE_REASONS = [
  { value: 'false_positive',         label: 'False positive — activity was legitimate' },
  { value: 'duplicate',              label: 'Duplicate — already actioned via another alert or case' },
  { value: 'escalated_to_case',      label: 'Escalated to case — now tracked in an investigation' },
  { value: 'no_further_action',      label: 'No further action — reviewed, no concern' },
  { value: 'threshold_needs_tuning', label: 'Threshold needs tuning — valid signal, rule too sensitive' },
  { value: 'other',                  label: 'Other (notes required)' },
]

function Card({ children, style = {} }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e8eef2',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 16,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

function FieldRowsTable({ rows, friendlyKeys = true }) {
  if (!rows?.length) return null
  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        overflow: 'hidden',
        fontSize: T.small,
        marginBottom: 12,
      }}
    >
      {rows.map((row, idx) => (
        <div
          key={`${row.key}-${idx}`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(120px, 1fr) 2fr',
            gap: 8,
            padding: '8px 12px',
            borderBottom: '1px solid #f1f5f9',
            background: '#fff',
          }}
        >
          <div style={{ color: C.slateL, wordBreak: 'break-word', fontWeight: 600 }}>
            {friendlyKeys ? displayTriggerFieldKey(row.key) : row.key}
          </div>
          <div style={{ wordBreak: 'break-word' }}>{row.value}</div>
        </div>
      ))}
    </div>
  )
}

function resolveEgmPk(alertRow, detailsParsed) {
  const d = detailsParsed || {}
  const td = d.triggered_data && typeof d.triggered_data === 'object' ? d.triggered_data : {}
  return (
    alertRow.egm_house_no ||
    td.EgmPK ||
    td.EgmHouseNo ||
    td.egmpk ||
    d.EgmPK ||
    d.EgmHouseNo ||
    ''
  )
}

/** Same local calendar day: "25 March 2026 07:45 to 19:45". Spans days: both date+time parts. */
function formatExpandedContextWindowRange(fromValue, toValue) {
  const from = new Date(fromValue)
  const to = new Date(toValue)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '—'
  const locale = 'en-AU'
  const datePart = (d) => d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
  const timePart = (d) => d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
  const df = datePart(from)
  const dt = datePart(to)
  const tf = timePart(from)
  const tt = timePart(to)
  if (df === dt) return `${df} ${tf} to ${tt}`
  return `${df} ${tf} to ${dt} ${tt}`
}

export default function AlertDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const [searchParams] = useSearchParams()
  const fromPipeline = searchParams.get('from') === 'pipeline'
  const canDeleteAlert =
    can('monitor-safe/alerts', 'delete')
    || can('monitor-safe/alerts', 'manage')
    || can('alerts', 'delete')
    || can('alerts', 'manage')
    || can('alerts', 'update')

  const [alert, setAlert] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [creatingCase, setCreatingCase] = useState(false)
  const [showDismissUi, setShowDismissUi] = useState(false)
  const [dismissReason, setDismissReason] = useState('')
  const [dismissing, setDismissing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [closureReason, setClosureReason] = useState('')
  const [gamingModal, setGamingModal] = useState(null)
  /** dataProvenance: where modal rows came from — api = gaming endpoints; alert_payload = reconstructed from alert.details */
  const [gamingModalData, setGamingModalData] = useState({
    loading: false,
    error: null,
    rows: [],
    hint: null,
    dataProvenance: null,
  })
  const [ruleDefinition, setRuleDefinition] = useState(null)
  const [ruleActions, setRuleActions] = useState(null)
  const [ruleTags, setRuleTags] = useState(null)
  const [ruleMetaLoading, setRuleMetaLoading] = useState(false)
  /** watchlist_alerts rows for this member (from overview); null = not loaded */
  const [watchlistPatronCount, setWatchlistPatronCount] = useState(null)
  /** Patron profile row from watchlist overview (name, etc.) */
  const [memberOverviewProfile, setMemberOverviewProfile] = useState(null)
  /** Echo of the member id used for GET …/overview (e.g. `M990928`); not the venue `profile.member_id`. */
  const [memberOverviewRequestedId, setMemberOverviewRequestedId] = useState('')
  /** Numeric patron risk 0–100 from patron_profiles (watchlist overview) */
  const [patronRiskScore, setPatronRiskScore] = useState(null)
  /** Patron risk tier (low/medium/high/critical) from same patron_profiles row as risk_score */
  const [patronRiskLevel, setPatronRiskLevel] = useState(null)
  const [patronComplianceStage, setPatronComplianceStage] = useState(null)
  const [patronCddTier, setPatronCddTier] = useState(null)
  /** Bump to refetch watchlist overview after display name save */
  const [memberOverviewTick, setMemberOverviewTick] = useState(0)
  const [patronOverviewLoading, setPatronOverviewLoading] = useState(false)
  /** Avoid clearing patron risk when only refetching overview (e.g. after recalc); still clear on member change. */
  const lastOverviewMemberIdRef = useRef(null)

  const load = useCallback(async () => {
    if (!id) {
      setLoading(false)
      setError('Missing alert id')
      setAlert(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/alerts/${encodeURIComponent(id)}`))
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load alert')
      setAlert(json.data)
    } catch (e) {
      setError(e.message || 'Failed to load')
      setAlert(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const selected = alert
  const backTo = fromPipeline ? '/compliance-safe/investigations/pipeline' : '/monitor-safe/alerts'
  const backLabel = fromPipeline ? 'Back to pipeline' : 'Back to alerts'

  const detailsParsed = useMemo(() => parseDetailsObject(selected?.details), [selected?.details])
  const triggerRows = useMemo(() => getTriggerEvidenceRows(detailsParsed), [detailsParsed])
  const triggeredDataObj = useMemo(() => {
    const td = detailsParsed?.triggered_data
    return td && typeof td === 'object' && !Array.isArray(td) ? td : null
  }, [detailsParsed])
  const fieldGroups = useMemo(() => groupTriggeredDataFields(triggeredDataObj), [triggeredDataObj])
  const combinedSplit = useMemo(() => splitCombinedTriggerRows(fieldGroups.combined), [fieldGroups.combined])
  const metaRows = useMemo(() => getDetailMetaRows(detailsParsed), [detailsParsed])
  const alertLinkageRows = useMemo(() => getAlertRowLinkageRows(selected), [selected])
  const egmPkForContext = selected ? resolveEgmPk(selected, detailsParsed) : ''

  const cumulativeMeterSnapshots = useMemo(
    () => extractCumulativeMeterSnapshots(detailsParsed),
    [detailsParsed]
  )

  const whyThisFired = useMemo(
    () => buildWhyThisFired(ruleDefinition, triggeredDataObj),
    [ruleDefinition, triggeredDataObj]
  )

  const cumulativeComparisonTable = useMemo(() => {
    if (!cumulativeMeterSnapshots.length) return null
    const cols = []
    if (triggeredDataObj) {
      cols.push({ data: triggeredDataObj, title: 'At trigger (primary)' })
    }
    cumulativeMeterSnapshots.forEach((s) => {
      cols.push({ data: s.data, title: `Reading ${s.index}` })
    })
    return buildCumulativeMeterComparisonTable(cols)
  }, [cumulativeMeterSnapshots, triggeredDataObj])

  /** When patron_profiles has no name, some feeds still carry a name on the trigger row. */
  const patronDisplayNameFromTrigger = useMemo(() => {
    const td = triggeredDataObj
    if (!td) return ''
    const get = (...keys) => {
      for (const k of keys) {
        const found = Object.keys(td).find((x) => x.toLowerCase() === String(k).toLowerCase())
        if (found != null && td[found] != null && String(td[found]).trim() !== '') return String(td[found]).trim()
      }
      return ''
    }
    const full = get('MemberName', 'PatronName', 'PlayerName', 'CustomerName', 'FullName')
    if (full) return full
    const fn = get('FirstName', 'GivenName')
    const ln = get('LastName', 'FamilyName', 'Surname')
    if (fn || ln) return [fn, ln].filter(Boolean).join(' ')
    return ''
  }, [triggeredDataObj])

  const patronDisplayNameResolved = useMemo(() => {
    const fn = memberOverviewProfile?.first_name
    const ln = memberOverviewProfile?.last_name
    const d = [fn, ln].filter(Boolean).join(' ').trim()
    if (d) return d
    return patronDisplayNameFromTrigger || ''
  }, [memberOverviewProfile, patronDisplayNameFromTrigger])

  /**
   * One member id for the sidebar/header: alert-style **M + club digits** when the id is
   * numeric club (M990828, 990828, MP…, V…+digits); matches backend `memberIdForAlertStorage`.
   * Otherwise profile or alert id as-is (UUID, M-DEMO-…, etc.).
   */
  const canonicalDisplayMemberId = useMemo(() => {
    const toNumericClub = (s) => {
      const t = String(s || '').trim()
      if (!t) return ''
      if (/^M\d+$/i.test(t)) {
        const n = t.replace(/^M/i, '').replace(/^0+/, '')
        return n === '' ? '0' : n
      }
      if (/^MP\d+$/i.test(t)) {
        const n = t.replace(/^MP/i, '').replace(/^0+/, '')
        return /^\d+$/.test(n) ? (n === '' ? '0' : n) : ''
      }
      if (/^V\d+$/i.test(t)) {
        const n = t.replace(/^V/i, '').replace(/^0+/, '')
        return /^\d+$/.test(n) ? (n === '' ? '0' : n) : ''
      }
      if (/^\d+$/.test(t)) {
        const n = t.replace(/^0+/, '')
        return n === '' ? '0' : n
      }
      return ''
    }
    const overviewReq = String(memberOverviewRequestedId || '').trim()
    if (overviewReq) {
      const rn = toNumericClub(overviewReq)
      if (rn && /^\d+$/.test(rn)) return `M${rn}`
      return overviewReq
    }
    const profileMid = String(memberOverviewProfile?.member_id || '').trim()
    const alertMid = String(selected?.member_id || '').trim()
    const pn = toNumericClub(profileMid)
    const an = toNumericClub(alertMid)
    const digits = pn || an
    if (digits && /^\d+$/.test(digits)) return `M${digits}`
    if (profileMid) return profileMid
    return alertMid
  }, [memberOverviewRequestedId, memberOverviewProfile?.member_id, selected?.member_id])

  const contextWindow = useMemo(() => {
    if (!selected) return null
    const resolved = resolveAlertGamingContextAnchor(selected, detailsParsed)
    if (!resolved?.anchor) return null
    const bounds = alertWindowBounds(resolved.anchor, { halfSpanMs: ALERT_GAMING_CONTEXT_HALF_MS })
    if (!bounds) return null
    return { ...bounds, anchorSource: resolved.source, anchor: resolved.anchor }
  }, [selected, detailsParsed])

  const austracBadgeInfo = useMemo(() => {
    const tagsApply = ruleTags && ruleHasAustracSmrTags(ruleTags)
    const smrWorkflow = Boolean(ruleActions?.startSMR)
    if (!tagsApply && !smrWorkflow) return { show: false, title: '' }
    const parts = []
    if (tagsApply) {
      const t = buildAustracTagTooltip(ruleTags)
      if (t) parts.push(`SMR / AUSTRAC metadata (rule builder tags):\n${t}`)
    }
    if (smrWorkflow) {
      parts.push('Rule action: Start SMR / AUSTRAC workflow when this rule fires (combined rule builder).')
    }
    return { show: true, title: parts.join('\n\n') }
  }, [ruleTags, ruleActions])

  const isCarded = Boolean(selected?.member_id && String(selected.member_id).trim() !== '')
  const statusNorm = String(selected?.status || '').toLowerCase()
  const isDismissed = statusNorm === 'dismissed'
  const canDismiss = !isDismissed && !selected?.case_id
  const canDelete = canDeleteAlert && !selected?.case_id

  const handleDeleteAlert = useCallback(async () => {
    if (!selected?.id || deleting) return
    const label = selected.rule_name || selected.rule_id || `#${selected.id}`
    if (!window.confirm(`Permanently delete alert ${label}?\n\nThis cannot be undone. Prefer Dismiss for normal compliance workflow.`)) {
      return
    }
    setDeleting(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/alerts/${selected.id}`), { method: 'DELETE' })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Delete failed')
      }
      toast.success('Alert deleted')
      navigate('/monitor-safe/alerts')
    } catch (e) {
      toast.error(e.message || 'Failed to delete alert')
    } finally {
      setDeleting(false)
    }
  }, [selected, deleting, navigate])

  useEffect(() => {
    if (!selected?.member_id || !isCarded) {
      setWatchlistPatronCount(null)
      setMemberOverviewProfile(null)
      setMemberOverviewRequestedId('')
      setPatronRiskScore(null)
      setPatronRiskLevel(null)
      setPatronComplianceStage(null)
      setPatronCddTier(null)
      lastOverviewMemberIdRef.current = null
      return
    }
    const overviewMid = String(selected.member_id || '').trim()
    const memberChangedForOverview = lastOverviewMemberIdRef.current !== overviewMid
    if (memberChangedForOverview) {
      lastOverviewMemberIdRef.current = overviewMid
      setPatronRiskScore(null)
      setPatronRiskLevel(null)
      setPatronComplianceStage(null)
      setPatronCddTier(null)
    }
    let cancelled = false
    setPatronOverviewLoading(true)
    authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(selected.member_id)}/overview`))
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j?.success) return
        setMemberOverviewRequestedId(
          String(j.data?.requested_member_id || j.data?.member_id || '').trim()
        )
        setMemberOverviewProfile(j.data?.profile && typeof j.data.profile === 'object' ? j.data.profile : null)
        const n = j.data?.watchlist?.count
        setWatchlistPatronCount(typeof n === 'number' ? n : null)
        const rs = j.data?.risk_score
        if (rs != null && rs !== '') {
          const v = Number(rs)
          setPatronRiskScore(Number.isFinite(v) ? v : null)
        } else {
          setPatronRiskScore(null)
        }
        const rlRaw = j.data?.risk_level
        let rlNorm = null
        if (typeof rlRaw === 'string' && rlRaw.trim()) rlNorm = rlRaw.trim()
        else if (typeof rlRaw === 'number' && Number.isFinite(rlRaw)) rlNorm = String(rlRaw)
        setPatronRiskLevel(rlNorm)
        setPatronComplianceStage(j.data?.compliance_stage || 'fresh')
        setPatronCddTier(j.data?.cdd_tier || null)
      })
      .catch(() => {
        if (!cancelled) {
          setWatchlistPatronCount(null)
          setMemberOverviewProfile(null)
          setMemberOverviewRequestedId('')
          setPatronRiskScore(null)
          setPatronRiskLevel(null)
          setPatronComplianceStage(null)
          setPatronCddTier(null)
        }
      })
      .finally(() => {
        if (!cancelled) setPatronOverviewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selected?.member_id, isCarded, memberOverviewTick])

  useEffect(() => {
    if (!selected?.rule_id) {
      setRuleDefinition(null)
      setRuleActions(null)
      setRuleTags(null)
      return
    }
    let cancelled = false
    setRuleMetaLoading(true)
    const rid = selected.rule_id
    Promise.all([
      authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(rid)}`)).then((r) => r.json()),
      authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(rid)}/actions`)).then((r) => r.json()),
      authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(rid)}/tags`)).then((r) => r.json()),
    ])
      .then(([jr, ja, jt]) => {
        if (cancelled) return
        setRuleDefinition(jr?.success && jr?.data ? jr.data : null)
        setRuleActions(ja?.success && ja?.data ? ja.data : null)
        setRuleTags(jt?.success && jt?.data ? jt.data : null)
      })
      .catch(() => {
        if (!cancelled) {
          setRuleDefinition(null)
          setRuleActions(null)
          setRuleTags(null)
        }
      })
      .finally(() => {
        if (!cancelled) setRuleMetaLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selected?.rule_id])

  useEffect(() => {
    if (!gamingModal || !selected || !contextWindow) return
    const from = contextWindow.from
    const to = contextWindow.to
    const egm = String(egmPkForContext || '').trim()
    const member = String(selected.member_id || '').trim()
    let cancelled = false
    setGamingModalData({ loading: true, error: null, rows: [], hint: null, dataProvenance: null })
    ;(async () => {
      try {
        if (gamingModal === 'meter' && !egm) {
          if (!cancelled) {
            setGamingModalData({
              loading: false,
              error: 'EgmPK is required to load meter readings for this window.',
              rows: [],
              hint: null,
              dataProvenance: null,
            })
          }
          return
        }
        const limit = '500'
        let path = ''
        if (gamingModal === 'sessions') {
          const qs = new URLSearchParams({ page: '1', limit, from, to })
          if (egm) qs.set('egm_pk', egm)
          if (member) qs.set('member_id', member)
          path = `/api/gaming/sessions?${qs}`
        } else if (gamingModal === 'meter') {
          const qs = new URLSearchParams({ page: '1', limit, from, to, egm_pk: egm })
          path = `/api/gaming/egms/readings?${qs}`
        } else if (gamingModal === 'combined') {
          const qs = new URLSearchParams({
            page: '1',
            limit,
            from,
            to,
            include_intervals: 'false',
          })
          if (egm) qs.set('egm_pk', egm)
          if (member) qs.set('member_id', member)
          path = `/api/gaming/combined/by-session?${qs}`
        } else {
          throw new Error('Unknown gaming context view')
        }
        const res = await authFetch(getLocalApiUrl(path))
        const json = await res.json()
        if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load data')
        if (cancelled) return
        let rows = Array.isArray(json.data) ? json.data : []
        let hint = json.hint || null
        let dataProvenance = rows.length > 0 ? 'api' : null
        if (rows.length === 0 && detailsParsed) {
          if (gamingModal === 'meter') {
            const fb = gamingModalMeterFallbackRows(detailsParsed, from, to)
            if (fb.rows.length) {
              rows = fb.rows
              hint = fb.hint || hint
              dataProvenance = 'alert_payload'
            }
          } else if (gamingModal === 'sessions') {
            const fb = gamingModalSessionsFallbackRows(detailsParsed, selected, from, to)
            if (fb.rows.length) {
              rows = fb.rows
              hint = fb.hint || hint
              dataProvenance = 'alert_payload'
            }
          } else if (gamingModal === 'combined') {
            const fb = gamingModalCombinedFallbackRows(detailsParsed, selected)
            if (fb.rows.length) {
              rows = fb.rows
              hint = fb.hint || hint
              dataProvenance = 'alert_payload'
            } else {
              hint =
                hint ||
                'No combined attribution in this window. Run Process & Combine for the trading day, or open full Gaming Data → Combined.'
            }
          }
        }
        if (gamingModal === 'combined' && rows.length > 1) {
          rows = [...rows].sort((a, b) => {
            const ta = new Date(a.SessionStart || a.FirstInterval || 0).getTime()
            const tb = new Date(b.SessionStart || b.FirstInterval || 0).getTime()
            return ta - tb
          })
        }
        setGamingModalData({
          loading: false,
          error: null,
          rows,
          hint,
          dataProvenance,
        })
      } catch (e) {
        if (!cancelled) {
          setGamingModalData({
            loading: false,
            error: e?.message || 'Failed to load',
            rows: [],
            hint: null,
            dataProvenance: null,
          })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [gamingModal, selected, contextWindow, egmPkForContext, detailsParsed])

  const gamingModalFullPageUrl = useMemo(() => {
    if (!selected || !contextWindow || !gamingModal) return null
    const tab = gamingModal === 'combined' ? 'combined' : gamingModal
    return buildGamingDataUrl({
      tab,
      egm_pk: egmPkForContext,
      member_id: selected.member_id || '',
      from: contextWindow.from,
      to: contextWindow.to,
      alertId: selected.id,
    })
  }, [selected, contextWindow, egmPkForContext, gamingModal])

  const meterModalMemberId = useMemo(
    () => resolveAlertMemberIdForMeterModal(selected, detailsParsed),
    [selected, detailsParsed]
  )

  const alertSessionPk = useMemo(
    () => resolveAlertSessionPk(selected, detailsParsed),
    [selected, detailsParsed]
  )

  const meterModalEvaluated = useMemo(() => {
    try {
      return getMeterModalEvaluatedContext(detailsParsed, selected)
    } catch (err) {
      console.error('getMeterModalEvaluatedContext failed', err)
      return null
    }
  }, [detailsParsed, selected])

  const triggerMeterHighlight = useMemo(() => {
    if (gamingModal !== 'meter' || gamingModalData.loading || !gamingModalData.rows.length) return null
    return findTriggerMeterRowIndex(gamingModalData.rows, detailsParsed, selected)
  }, [gamingModal, gamingModalData.loading, gamingModalData.rows, detailsParsed, selected])

  const triggerCombinedHighlight = useMemo(() => {
    if (
      (gamingModal !== 'combined' && gamingModal !== 'sessions') ||
      gamingModalData.loading ||
      !gamingModalData.rows.length
    ) {
      return null
    }
    return findTriggerCombinedRowIndex(gamingModalData.rows, detailsParsed, selected)
  }, [gamingModal, gamingModalData.loading, gamingModalData.rows, detailsParsed, selected])

  const triggerMeterRowRef = useRef(null)
  const triggerCombinedRowRef = useRef(null)
  useEffect(() => {
    if (gamingModal !== 'meter' || !triggerMeterHighlight) return
    const t = window.setTimeout(() => {
      triggerMeterRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 80)
    return () => window.clearTimeout(t)
  }, [gamingModal, triggerMeterHighlight?.index, gamingModalData.rows])

  useEffect(() => {
    if ((gamingModal !== 'combined' && gamingModal !== 'sessions') || !triggerCombinedHighlight) return
    const t = window.setTimeout(() => {
      triggerCombinedRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 80)
    return () => window.clearTimeout(t)
  }, [gamingModal, triggerCombinedHighlight?.index, gamingModalData.rows])

  if (loading && !alert) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: C.slate, fontFamily: "'Poppins', system-ui, sans-serif" }}>
        Loading alert…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: C.red, fontFamily: "'Poppins', system-ui, sans-serif" }}>
        {error}
        <div style={{ marginTop: 16 }}>
          <Link to={backTo} style={{ color: '#475569', fontWeight: 600 }}>
            ← {backLabel}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        fontFamily: "'Poppins', system-ui, sans-serif",
        minHeight: '100vh',
        padding: '16px 24px',
        paddingBottom: selected ? 108 : 24,
        maxWidth: 1320,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <style>
        {`
        .alert-detail-cols {
          display: grid;
          grid-template-columns: minmax(260px, 380px) minmax(0, 1fr);
          gap: 20px;
          align-items: start;
        }
        @media (max-width: 900px) {
          .alert-detail-cols {
            grid-template-columns: 1fr;
          }
        }
      `}
      </style>

      {selected && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <div style={{ marginBottom: 18 }}>
              <Link
                to={backTo}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: T.normal,
                  color: C.slate,
                  textDecoration: 'none',
                  fontWeight: 600,
                }}
              >
                <ChevronLeft size={16} /> {backLabel}
              </Link>
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1.25 }}>
                  {selected.rule_name || 'Alert'}
                </div>
                <div style={{ fontSize: T.small, color: C.slateL, marginTop: 6 }}>
                  Alert #{selected.id} · {selected.rule_id || '—'}
                </div>
                {!isCarded ? (
                  <div style={{ fontSize: T.small, color: C.slateL, marginTop: 8, lineHeight: 1.45 }}>
                    <span style={{ fontWeight: 700, color: '#64748b' }}>Uncarded</span>
                    {patronDisplayNameFromTrigger ? (
                      <>
                        {' '}
                        · <span style={{ fontWeight: 600, color: '#334155' }}>{patronDisplayNameFromTrigger}</span>{' '}
                        <span style={{ fontStyle: 'italic', color: C.slateL }}>(trigger data)</span>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
          </div>

          <div className="alert-detail-cols">
            {/* Left panel: summary */}
            <Card style={{ marginBottom: 0, minWidth: 0 }}>
            <aside
              style={{
                padding: '14px 18px',
              }}
            >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
              {selected.status && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    padding: '4px 10px',
                    borderRadius: 999,
                    background:
                      statusNorm === 'open'
                        ? '#ecfdf5'
                        : statusNorm === 'dismissed'
                          ? '#fef3c7'
                          : '#f1f5f9',
                    color:
                      statusNorm === 'open'
                        ? '#047857'
                        : statusNorm === 'dismissed'
                          ? '#b45309'
                          : '#475569',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  {selected.status}
                </span>
              )}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: '#f8fafc',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                }}
                title="Whether the patron was identified on this event — informational only, not a risk score by itself"
              >
                {isCarded ? 'Carded' : 'Uncarded'}
              </span>
              {isCarded && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    padding: '4px 10px',
                    borderRadius: 999,
                    background:
                      watchlistPatronCount != null && watchlistPatronCount > 0 ? '#fef3c7' : '#f1f5f9',
                    color: watchlistPatronCount != null && watchlistPatronCount > 0 ? '#b45309' : '#64748b',
                    border: '1px solid #e2e8f0',
                  }}
                  title="Watchlist entries for this member (watchlist_alerts)"
                >
                  {patronOverviewLoading
                    ? 'Watchlist…'
                    : watchlistPatronCount != null && watchlistPatronCount > 0
                      ? `On watchlist (${watchlistPatronCount})`
                      : 'Not on watchlist'}
                </span>
              )}
              {selected?.rule_id && austracBadgeInfo.show && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    padding: '4px 10px',
                    borderRadius: 999,
                    background: '#e0f2fe',
                    color: '#0369a1',
                    border: '1px solid #7dd3fc',
                  }}
                  title={austracBadgeInfo.title}
                >
                  AUSTRAC
                </span>
              )}
            </div>

            {isCarded && (
              <div
                style={{
                  marginBottom: 16,
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #e0e7ff',
                  background: '#f8fafc',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                  Patron
                </div>
                {patronOverviewLoading && (
                  <div style={{ fontSize: 11, color: C.slateL }}>Loading patron…</div>
                )}
                {!patronOverviewLoading && (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', lineHeight: 1.35, marginBottom: 6 }}>
                      {patronDisplayNameResolved || '—'}
                    </div>
                    {patronDisplayNameFromTrigger && patronDisplayNameResolved === patronDisplayNameFromTrigger && (
                      <div style={{ fontSize: 10, color: C.slateL, marginBottom: 8, lineHeight: 1.4, fontStyle: 'italic' }}>
                        Name from alert trigger data
                      </div>
                    )}
                    {!patronDisplayNameResolved && (
                      <div style={{ fontSize: 11, color: C.slateL, marginBottom: 8, fontStyle: 'italic', lineHeight: 1.4 }}>
                        No name on profile or trigger data
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: C.slateL, marginTop: 4, marginBottom: 4 }}>Member #</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                      {memberOverviewProfile?.master_profile_id && (
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            fontSize: 12,
                            color: '#475569',
                            background: '#f1f5f9',
                            padding: '2px 6px',
                            borderRadius: 4,
                            display: 'inline-block',
                            width: 'fit-content',
                          }}
                        >
                          {memberOverviewProfile.master_profile_id}
                        </div>
                      )}
                      {memberOverviewProfile?.member_id && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Users size={11} color="#3b82f6" />
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontSize: 11,
                              color: '#1d4ed8',
                              background: '#eff6ff',
                              padding: '1px 5px',
                              borderRadius: 3,
                            }}
                          >
                            {memberOverviewProfile.member_id}
                          </span>
                        </div>
                      )}
                      {memberOverviewProfile?.visitor_id && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <User size={11} color="#d97706" />
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontSize: 11,
                              color: '#b45309',
                              background: '#fffbeb',
                              padding: '1px 5px',
                              borderRadius: 3,
                            }}
                          >
                            {memberOverviewProfile.visitor_id}
                          </span>
                        </div>
                      )}
                      {!memberOverviewProfile?.master_profile_id && !memberOverviewProfile?.member_id && (
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            fontSize: 12,
                            color: '#475569',
                            wordBreak: 'break-all',
                          }}
                        >
                          {canonicalDisplayMemberId}
                        </div>
                      )}
                    </div>
                    <PatronComplianceStrip
                      size="compact"
                      complianceStage={patronComplianceStage}
                      riskScore={patronRiskScore}
                      riskLevel={patronRiskLevel}
                      cddTier={patronCddTier}
                    />
                    <Link
                      to={
                        watchlistPatronCount != null && watchlistPatronCount > 0
                          ? `/monitor-safe/watch-list/${encodeURIComponent(selected.member_id)}`
                          : `/profile-safe/search?memberId=${encodeURIComponent(canonicalDisplayMemberId || selected.member_id)}`
                      }
                      title={
                        watchlistPatronCount != null && watchlistPatronCount > 0
                          ? 'Watch list alerts and patron overview for this member'
                          : 'Open Profile Search for this member'
                      }
                      style={{ display: 'inline-block', marginTop: 12, fontSize: T.small, fontWeight: 700, color: C.pri }}
                    >
                      {watchlistPatronCount != null && watchlistPatronCount > 0
                        ? 'Open watchlist profile →'
                        : 'View member detail →'}
                    </Link>
                  </>
                )}
              </div>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 14,
                fontSize: T.normal,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                Alert
              </div>
              <div>
                <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 6 }}>Risk &amp; severity (rule builder)</div>
                {ruleMetaLoading ? (
                  <div style={{ fontSize: T.small, color: C.slateL }}>Loading rule…</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {ruleActions?.risk_level != null && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          padding: '5px 10px',
                          borderRadius: 8,
                          border: `1px solid ${riskTierStyle(ruleActions.risk_level).border}`,
                          background: riskTierStyle(ruleActions.risk_level).bg,
                          color: riskTierStyle(ruleActions.risk_level).color,
                        }}
                        title="Risk Assessment tier from the combined rule builder (Rule actions)"
                      >
                        Risk: {formatRiskTierLabel(ruleActions.risk_level)}
                      </span>
                    )}
                    <span style={{ fontSize: T.small, color: C.slate }}>
                      Rule severity:{' '}
                      <strong>{formatSeverityLabel(ruleDefinition?.severity || selected.severity)}</strong>
                    </span>
                    <span style={{ fontSize: T.small, color: C.slate }}>
                      · This alert:{' '}
                      <strong>{formatSeverityLabel(selected.severity)}</strong>
                    </span>
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Rule that triggered</div>
                <div style={{ fontWeight: 700, color: '#1e293b' }}>{selected.rule_name || '—'}</div>
                <div style={{ fontSize: T.small, fontFamily: 'monospace', marginTop: 4 }}>{selected.rule_id || '—'}</div>
                {ruleTags && formatTagLines(ruleTags).length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 6 }}>
                      Rule tags (offence / service / suspicion — same as builder)
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: T.small, color: '#334155' }}>
                      {formatTagLines(ruleTags).map((t, i) => (
                        <li key={`${t.group}-${i}`}>
                          <span style={{ color: C.slateL }}>{t.group}: </span>
                          {t.line}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Data source</div>
                <div>{formatRuleTypeUi(selected.rule_type)}</div>
                <div style={{ fontSize: 11, color: C.slateL, marginTop: 4 }}>
                  Engine type: <span style={{ fontFamily: 'monospace' }}>{selected.rule_type || '—'}</span>
                </div>
              </div>

              <div>
                <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>When triggered</div>
                <div>
                  <span style={{ color: C.slateL }}>First seen: </span>
                  {selected.first_seen ? formatSydneyDateTime(selected.first_seen) : '—'}
                </div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ color: C.slateL }}>Last updated: </span>
                  {selected.last_updated ? formatSydneyDateTime(selected.last_updated) : '—'}
                </div>
              </div>

              <div>
                <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>EGM</div>
                <div style={{ fontFamily: 'monospace' }}>{selected.egm_house_no || egmPkForContext || '—'}</div>
              </div>

              <div>
                <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Evaluation grain</div>
                <div style={{ fontWeight: 700 }}>
                  {ruleDefinition?.processing_category
                    ? formatProcessingCategoryLabel(ruleDefinition.processing_category)
                    : ruleMetaLoading
                      ? '…'
                      : '—'}
                </div>
                {processingCategoryExplanation(ruleDefinition?.processing_category) && (
                  <div style={{ fontSize: T.small, color: C.slate, marginTop: 6, lineHeight: 1.45 }}>
                    {processingCategoryExplanation(ruleDefinition.processing_category)}
                  </div>
                )}
              </div>

              {!isCarded && (
                <div
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                  }}
                >
                  <div style={{ fontSize: T.small, fontWeight: 700, color: C.slate, marginBottom: 8 }}>Patron (uncarded)</div>
                  <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Member #</div>
                  <div style={{ fontFamily: 'monospace', marginBottom: patronDisplayNameFromTrigger ? 10 : 0 }}>Unidentified</div>
                  {patronDisplayNameFromTrigger ? (
                    <div>
                      <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Name (from trigger data)</div>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{patronDisplayNameFromTrigger}</div>
                    </div>
                  ) : null}
                </div>
              )}
              <div>
                <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Transaction / session (alert row)</div>
                <div style={{ fontFamily: 'monospace' }}>
                  tx {selected.transaction_id || '—'} · session {selected.session_id || '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Venue</div>
                <div style={{ fontFamily: 'monospace' }}>
                  {selected.venue_name || selected.venue_code_raw || selected.venue_code || '—'}
                </div>
              </div>
              {selected.cluster_id && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Cluster</div>
                  <div style={{ fontFamily: 'monospace', fontSize: T.small, color: '#2563eb' }}>{selected.cluster_id}</div>
                </div>
              )}
              {(() => {
                const extra =
                  typeof selected.extra === 'string'
                    ? (() => {
                        try {
                          return JSON.parse(selected.extra || '{}')
                        } catch {
                          return {}
                        }
                      })()
                    : selected.extra || {}
                const count = extra.trigger_count
                const policyMode = extra.alert_policy_mode
                const cumAmount = extra.cumulative_amount
                const confidence = extra.confidence
                const lastTriggered = extra.last_triggered
                return (
                  <>
                    {count > 1 && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Trigger count</div>
                        <div style={{ color: '#d97706', fontWeight: 600 }}>
                          Triggered {count} times
                          {lastTriggered ? ` (last: ${formatSydneyDateTime(lastTriggered)})` : ''}
                        </div>
                      </div>
                    )}
                    {cumAmount != null && (
                      <div>
                        <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Cumulative Amount</div>
                        <div style={{ fontWeight: 700 }}>${Number(cumAmount).toLocaleString()}</div>
                      </div>
                    )}
                    {confidence != null && confidence < 100 && (
                      <div>
                        <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Confidence</div>
                        <div style={{ fontFamily: 'monospace', fontSize: T.small }}>{confidence}%</div>
                      </div>
                    )}
                    {policyMode && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Alert Policy</div>
                        <div style={{ fontFamily: 'monospace', fontSize: T.small }}>
                          {policyMode}
                          {extra.alert_policy_n ? ` (N=${extra.alert_policy_n})` : ''}
                        </div>
                      </div>
                    )}
                  </>
                )
              })()}
              {selected.case_id && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>Investigation / case</div>
                  <Link
                    to={`/compliance-safe/investigations/${encodeURIComponent(selected.case_number || selected.case_id)}`}
                    style={{ fontSize: T.normal, fontWeight: 600, color: C.pri, textDecoration: 'underline' }}
                  >
                    Open {selected.case_number || selected.case_id}
                  </Link>
                </div>
              )}
            </div>
            </aside>
            </Card>

            {/* Right panel: evidence & context */}
            <Card
              style={{
                marginBottom: 0,
                minWidth: 0,
              }}
            >
            <div
              style={{
                padding: '14px 18px',
              }}
            >
            {(whyThisFired || ruleMetaLoading) && (
              <div
                style={{
                  marginBottom: 20,
                  padding: 16,
                  borderRadius: 10,
                  border: '1px solid #cce5dc',
                  background: 'linear-gradient(180deg, #f0fdf8 0%, #ffffff 100%)',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
              >
                <div style={{ fontSize: T.normal, color: C.pri, marginBottom: 6, fontWeight: 800 }}>
                  Why this fired
                </div>
                {ruleMetaLoading && !whyThisFired ? (
                  <div style={{ fontSize: T.small, color: C.slate }}>Loading rule conditions…</div>
                ) : (
                  <>
                    <div style={{ fontSize: T.normal, color: '#1e293b', fontWeight: 600, marginBottom: 10, lineHeight: 1.4 }}>
                      {whyThisFired.summary}
                    </div>
                    <div style={{ fontSize: 11, color: C.slateL, marginBottom: 8 }}>
                      {selected.rule_name || 'Rule'}{' '}
                      <span style={{ fontFamily: 'monospace' }}>({selected.rule_id || '—'})</span>
                      {whyThisFired.rows.length > 1 && (
                        <> · match when <strong>{whyThisFired.join.toUpperCase()}</strong> conditions pass</>
                      )}
                    </div>
                    {whyThisFired.rows.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {whyThisFired.rows.map((row, i) => {
                          const ok = row.passed === true
                          const fail = row.passed === false
                          const border = ok ? '#86efac' : fail ? '#fca5a5' : '#e2e8f0'
                          const bg = ok ? '#f0fdf4' : fail ? '#fef2f2' : '#f8fafc'
                          const badge = ok ? '✓' : fail ? '✗' : '·'
                          const badgeColor = ok ? '#15803d' : fail ? '#b91c1c' : C.slate
                          return (
                            <div
                              key={`${row.label}-${i}`}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '22px 1fr auto',
                                gap: 10,
                                alignItems: 'center',
                                padding: '8px 10px',
                                borderRadius: 8,
                                border: `1px solid ${border}`,
                                background: bg,
                              }}
                            >
                              <span style={{ fontWeight: 800, color: badgeColor, textAlign: 'center' }}>{badge}</span>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: T.small, fontWeight: 700, color: '#1e293b' }}>{row.label}</div>
                                {row.expression && row.expression !== row.label && (
                                  <div style={{ fontSize: 10, fontFamily: 'monospace', color: C.slateL, marginTop: 2 }}>
                                    {row.expression}
                                  </div>
                                )}
                              </div>
                              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <div style={{ fontSize: T.small, fontWeight: 800, color: '#0f172a' }}>{row.actual}</div>
                                {row.expected && (
                                  <div style={{ fontSize: 10, color: C.slateL }}>need {row.expected}</div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {contextWindow && (
              <div
                style={{
                  marginBottom: 20,
                  padding: 16,
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
              >
                <div style={{ fontSize: T.normal, color: C.pri, marginBottom: 10, fontWeight: 800 }}>
                  Expanded context (±6 hours from {contextWindow.anchorSource || 'trigger time'})
                </div>
                <div style={{ fontSize: T.small, color: C.slate, marginBottom: 10 }}>
                  Window: {formatExpandedContextWindowRange(contextWindow.from, contextWindow.to)}
                  {!egmPkForContext && (
                    <span style={{ color: '#b45309', display: 'block', marginTop: 6 }}>
                      No EgmPK on alert or in details — add EGM to the alert or open Gaming Data and filter manually.
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    disabled={!egmPkForContext}
                    title={!egmPkForContext ? 'EgmPK required — not on this alert' : undefined}
                    onClick={() => setGamingModal('meter')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 12px',
                      background: '#fff',
                      color: egmPkForContext ? C.slate : C.slateL,
                      border: '1px solid #e2e8f0',
                      borderRadius: 7,
                      fontSize: T.small,
                      fontWeight: 700,
                      cursor: egmPkForContext ? 'pointer' : 'not-allowed',
                      opacity: egmPkForContext ? 1 : 0.65,
                    }}
                  >
                    Meter readings
                  </button>
                  <button
                    type="button"
                    onClick={() => setGamingModal('sessions')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 12px',
                      background: '#fff',
                      color: C.slate,
                      border: '1px solid #e2e8f0',
                      borderRadius: 7,
                      fontSize: T.small,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Gaming sessions
                  </button>
                  <button
                    type="button"
                    onClick={() => setGamingModal('combined')}
                    title="Meter×session attribution (CashIn_alloc) — what Process & Combine builds for rules"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 12px',
                      background: '#fff',
                      color: C.slate,
                      border: '1px solid #e2e8f0',
                      borderRadius: 7,
                      fontSize: T.small,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Combined sits
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: 0 }}>
              {!triggeredDataObj && alertLinkageRows.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: T.small, fontWeight: 800, color: '#92400e', marginBottom: 6 }}>
                    Alert linkage only (no feed row in details)
                  </div>
                  <p style={{ fontSize: T.small, color: C.slate, margin: '0 0 8px', lineHeight: 1.45 }}>
                    The alert record has venue / member / session / EGM linkage, but <code style={{ fontSize: 11 }}>triggered_data</code>{' '}
                    was not stored — you will not see meter counters or session timings here until the engine persists the feed
                    row.
                  </p>
                  <FieldRowsTable rows={alertLinkageRows} friendlyKeys={false} />
                </div>
              )}

              {triggeredDataObj ? (
                <>
                  {fieldGroups.shape === 'combined' && fieldGroups.combined.length > 0 && (
                    <>
                      {combinedSplit.allocation.length > 0 && (
                        <>
                          <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                            Metering (interval allocation)
                          </div>
                          <p style={{ fontSize: 11, color: C.slateL, margin: '0 0 8px' }}>
                            Allocated meter counters on the interval (overlap of session and meter timeline).
                          </p>
                          <FieldRowsTable rows={combinedSplit.allocation} />
                        </>
                      )}
                      {combinedSplit.gaming.length > 0 && (
                        <>
                          <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                            Gaming (session linkage)
                          </div>
                          <p style={{ fontSize: 11, color: C.slateL, margin: '0 0 8px' }}>
                            Session and member context tied to that interval.
                          </p>
                          <FieldRowsTable rows={combinedSplit.gaming} />
                        </>
                      )}
                    </>
                  )}

                  {fieldGroups.shape === 'meter' && fieldGroups.meter.length > 0 && (
                    <>
                      <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                        Metering (EGM snapshot at trigger)
                      </div>
                      <FieldRowsTable rows={fieldGroups.meter} />
                    </>
                  )}

                  {fieldGroups.shape === 'session' && fieldGroups.session.length > 0 && (
                    <>
                      <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                        Gaming session (at trigger)
                      </div>
                      <FieldRowsTable rows={fieldGroups.session} />
                    </>
                  )}

                  {fieldGroups.shape === 'transaction' && (fieldGroups.session.length > 0 || fieldGroups.meter.length > 0) && (
                    <>
                      {fieldGroups.session.length > 0 && (
                        <>
                          <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                            Transaction / session fields
                          </div>
                          <FieldRowsTable rows={fieldGroups.session} />
                        </>
                      )}
                      {fieldGroups.meter.length > 0 && (
                        <>
                          <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                            Related meter-style fields
                          </div>
                          <FieldRowsTable rows={fieldGroups.meter} />
                        </>
                      )}
                    </>
                  )}

                  {fieldGroups.shape === 'unknown' &&
                    (fieldGroups.meter.length > 0 ||
                      fieldGroups.session.length > 0 ||
                      fieldGroups.combined.length > 0) && (
                      <>
                        {fieldGroups.combined.length > 0 && (
                          <>
                            <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                              Attribution-style fields
                            </div>
                            <FieldRowsTable rows={fieldGroups.combined} />
                          </>
                        )}
                        {fieldGroups.meter.length > 0 && (
                          <>
                            <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                              Meter-style fields
                            </div>
                            <FieldRowsTable rows={fieldGroups.meter} />
                          </>
                        )}
                        {fieldGroups.session.length > 0 && (
                          <>
                            <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                              Session-style fields
                            </div>
                            <FieldRowsTable rows={fieldGroups.session} />
                          </>
                        )}
                      </>
                    )}

                  {fieldGroups.other.length > 0 && (
                    <>
                      <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                        Other fields on trigger record
                      </div>
                      <FieldRowsTable rows={fieldGroups.other} />
                    </>
                  )}

                </>
              ) : (
                <>
                  {triggerRows.length > 0 ? (
                    <>
                      <div style={{ fontSize: T.small, fontWeight: 700, color: C.slate, marginBottom: 8 }}>
                        Evaluated fields (no <code style={{ fontSize: 11 }}>triggered_data</code> object — showing
                        flattened details)
                      </div>
                      <FieldRowsTable rows={triggerRows.map((r) => ({ key: r.key, value: r.value }))} />
                    </>
                  ) : (
                    <div style={{ fontSize: T.small, color: C.slateL, fontStyle: 'italic' }}>
                      {alertLinkageRows.length === 0
                        ? 'No feed row or linkage fields on this alert.'
                        : 'No extra flattened fields — see linkage above.'}
                    </div>
                  )}
                </>
              )}

              {cumulativeComparisonTable && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: T.normal, color: '#1e293b', marginBottom: 6, fontWeight: 800 }}>
                    Cumulative meter evidence (evaluation window)
                  </div>
                  <div
                    style={{
                      overflowX: 'auto',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      background: '#fff',
                    }}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: T.small,
                        minWidth: 480,
                      }}
                    >
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th
                            style={{
                              textAlign: 'left',
                              padding: '10px 12px',
                              borderBottom: '1px solid #e2e8f0',
                              position: 'sticky',
                              left: 0,
                              top: 0,
                              background: '#f8fafc',
                              zIndex: 2,
                              fontWeight: 800,
                              color: '#475569',
                              whiteSpace: 'nowrap',
                              minWidth: 120,
                            }}
                          >
                            Reading
                          </th>
                          {cumulativeComparisonTable.rowLabels.map((label, li) => {
                            const rawKey = Object.keys(cumulativeComparisonTable.rows[0] || {})[li]
                            const tooltip = rawKey ? getFieldTooltip(label) : null
                            return (
                              <th
                                key={`cum-field-${li}`}
                                style={{
                                  textAlign: 'left',
                                  padding: '10px 12px',
                                  borderBottom: '1px solid #e2e8f0',
                                  borderLeft: '1px solid #f1f5f9',
                                  fontWeight: 800,
                                  color: '#1e293b',
                                  whiteSpace: 'nowrap',
                                  minWidth: 100,
                                  position: 'sticky',
                                  top: 0,
                                  background: '#f8fafc',
                                  zIndex: 1,
                                }}
                                title={tooltip || label}
                              >
                                {label}
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {cumulativeComparisonTable.headers.map((header, hi) => {
                          // Extract SessionPK from this reading to group related readings
                          const sessionPkIdx = cumulativeComparisonTable.rowLabels.findIndex(
                            label => label.toLowerCase().includes('session pk')
                          );
                          const sessionPk = sessionPkIdx >= 0 ? cumulativeComparisonTable.rows[sessionPkIdx][hi] : null;
                          
                          // Find if other readings share this session
                          const otherReadingsWithSameSession = sessionPk && sessionPk !== '—'
                            ? cumulativeComparisonTable.headers.filter((_, otherHi) => {
                                return otherHi !== hi && 
                                       sessionPkIdx >= 0 && 
                                       cumulativeComparisonTable.rows[sessionPkIdx][otherHi] === sessionPk;
                              })
                            : [];
                          
                          const hasSessionGroup = otherReadingsWithSameSession.length > 0;
                          const rowBg = hi % 2 === 0 ? '#fff' : '#fafbfc';
                          const groupedBg = hi % 2 === 0 ? '#f0f9ff' : '#e0f2fe'; // Blue tint for grouped sessions
                          
                          return (
                            <tr key={`cum-reading-${hi}`} style={{ background: hasSessionGroup ? groupedBg : rowBg }}>
                              <td
                                style={{
                                  padding: '8px 12px',
                                  borderBottom: '1px solid #f1f5f9',
                                  position: 'sticky',
                                  left: 0,
                                  background: hasSessionGroup ? groupedBg : rowBg,
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  verticalAlign: 'top',
                                  whiteSpace: 'nowrap',
                                  zIndex: 1,
                                  borderLeft: hasSessionGroup ? '3px solid #0ea5e9' : 'none',
                                }}
                              >
                                {header}
                                {hasSessionGroup && (
                                  <span 
                                    style={{ 
                                      fontSize: 10, 
                                      color: '#0ea5e9', 
                                      marginLeft: 6,
                                      fontWeight: 600
                                    }}
                                    title={`Same gaming session: ${sessionPk}`}
                                  >
                                    🔗
                                  </span>
                                )}
                              </td>
                              {cumulativeComparisonTable.rows.map((row, ri) => (
                                <td
                                  key={`cum-cell-${hi}-${ri}`}
                                  style={{
                                    padding: '8px 12px',
                                    borderBottom: '1px solid #f1f5f9',
                                    borderLeft: '1px solid #f1f5f9',
                                    wordBreak: 'break-word',
                                    verticalAlign: 'top',
                                    color: '#1e293b',
                                  }}
                                >
                                  {row[hi]}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {metaRows.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: T.small, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>
                    Engine metadata (on alert details)
                  </div>
                  <p style={{ fontSize: 11, color: C.slateL, margin: '0 0 8px' }}>
                    Not part of the venue feed row; added by the rules engine when the alert was written.
                  </p>
                  <FieldRowsTable rows={metaRows} />
                </div>
              )}
            </div>
            </div>
            </Card>
          </div>

          <div
            role="region"
            aria-label="Alert actions"
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 25,
              marginTop: 12,
              marginLeft: -24,
              marginRight: -24,
              padding: '12px 24px calc(12px + env(safe-area-inset-bottom, 0px))',
              background: 'rgba(255,255,255,0.98)',
              borderTop: '1px solid #e2e8f0',
              boxShadow: '0 -6px 24px rgba(15, 23, 42, 0.08)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {showDismissUi && (
              <div
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #fcd34d',
                  background: '#fffbeb',
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: T.small, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
                  Dismiss alert (SVCP §5.2 structured reason required)
                </div>
                <label style={{ display: 'block', fontSize: T.small, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>
                  Closure reason <span style={{ color: '#b91c1c' }}>*</span>
                </label>
                <select
                  value={closureReason}
                  onChange={(e) => setClosureReason(e.target.value)}
                  style={{
                    width: '100%',
                    fontSize: T.small,
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid #fcd34d',
                    marginBottom: 8,
                    fontFamily: 'inherit',
                    background: '#fff',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="">Select a reason…</option>
                  {CLOSURE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <textarea
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                  placeholder={
                    closureReason === 'other'
                      ? 'Notes required when closure reason is "Other"'
                      : 'Optional notes (stored on alert remarks and as closure context)'
                  }
                  rows={2}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    fontSize: T.small,
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid #fcd34d',
                    marginBottom: 8,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(() => {
                    const notesRequired = closureReason === 'other' && dismissReason.trim().length === 0
                    const disabled = dismissing || !closureReason || notesRequired
                    return (
                      <button
                        type="button"
                        disabled={disabled}
                        title={!closureReason ? 'Pick a closure reason first' : notesRequired ? 'Notes are required for "Other"' : undefined}
                        onClick={async () => {
                          setDismissing(true)
                          try {
                            const res = await authFetch(getLocalApiUrl(`/api/alerts/${selected.id}/workflow`), {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                status: 'dismissed',
                                closure_reason: closureReason,
                                closure_reason_notes: dismissReason.trim() || undefined,
                                remarks: dismissReason.trim() || undefined,
                              }),
                            })
                            const json = await res.json()
                            if (!res.ok || !json.success) throw new Error(json.error || 'Failed to dismiss')
                            toast.success('Alert dismissed')
                            setShowDismissUi(false)
                            setDismissReason('')
                            setClosureReason('')
                            await load()
                          } catch (e) {
                            toast.error(e?.message || 'Failed to dismiss')
                          } finally {
                            setDismissing(false)
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#b45309',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 7,
                          fontSize: T.small,
                          fontWeight: 700,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.6 : 1,
                        }}
                      >
                        {dismissing ? 'Dismissing…' : 'Confirm dismiss'}
                      </button>
                    )
                  })()}
                  <button
                    type="button"
                    disabled={dismissing}
                    onClick={() => {
                      setShowDismissUi(false)
                      setDismissReason('')
                      setClosureReason('')
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#fff',
                      color: C.slate,
                      border: '1px solid #e2e8f0',
                      borderRadius: 7,
                      fontSize: T.small,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                {!isCarded && (
                  <span style={{ fontSize: T.small, color: C.slateL }}>
                    Unidentified — add to watchlist when patron is known
                  </span>
                )}
                {isDismissed && (
                  <span style={{ fontSize: T.small, color: C.slateL, fontStyle: 'italic' }}>This alert was dismissed.</span>
                )}
                {!canDismiss && !isDismissed && selected.case_id && (
                  <span style={{ fontSize: T.small, color: C.slateL }} title="Dismiss is disabled while an investigation is linked">
                    Dismiss unavailable — investigation linked
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
                {isCarded && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const payload = {
                          member_id: selected.member_id,
                          alert_type: selected.rule_type?.toUpperCase() || 'TRANSACTION',
                          severity: selected.severity?.toLowerCase() || 'high',
                          status: 'open',
                          notes: `Auto-added from alert: ${selected.rule_name}`,
                          rule_id: selected.rule_id,
                        }
                        const res = await authFetch(getLocalApiUrl('/api/watchlist'), {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(payload),
                        })
                        const json = await res.json()
                        if (json.success) {
                          toast.success('Added to watchlist')
                          setMemberOverviewTick((t) => t + 1)
                        } else {
                          toast.error(json.error || 'Failed to add to watchlist')
                        }
                      } catch (e) {
                        toast.error(e.message)
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#fff',
                      color: C.slate,
                      border: '1px solid #e2e8f0',
                      borderRadius: 7,
                      fontSize: T.small,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Add to watchlist
                  </button>
                )}
                <button
                  type="button"
                  disabled={!!selected.case_id || creatingCase}
                  onClick={async () => {
                    if (selected.case_id) return
                    setCreatingCase(true)
                    try {
                      const res = await authFetch(getLocalApiUrl(`/api/alerts/${selected.id}/create-case`), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({}),
                      })
                      const json = await res.json()
                      if (json.success && json.data) {
                        await load()
                        navigate(`/compliance-safe/investigations/${json.data.case_number || json.data.id}`)
                      } else {
                        toast.error(json.error || 'Failed to create case')
                      }
                    } catch (e) {
                      toast.error(e?.message || 'Failed to create case')
                    } finally {
                      setCreatingCase(false)
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    background: selected.case_id || creatingCase ? '#e2e8f0' : C.pri,
                    color: selected.case_id || creatingCase ? C.slateL : '#fff',
                    border: 'none',
                    borderRadius: 7,
                    fontSize: T.small,
                    fontWeight: 700,
                    cursor: selected.case_id || creatingCase ? 'not-allowed' : 'pointer',
                  }}
                >
                  {creatingCase ? 'Creating…' : selected.case_id ? 'Investigation linked' : 'Start investigation'}
                </button>
                {canDismiss && (
                  <button
                    type="button"
                    disabled={dismissing}
                    onClick={() => setShowDismissUi((s) => !s)}
                    style={{
                      padding: '8px 16px',
                      background: showDismissUi ? '#fef3c7' : '#fff',
                      color: '#92400e',
                      border: '1px solid #fcd34d',
                      borderRadius: 7,
                      fontSize: T.small,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {showDismissUi ? 'Dismiss — hide form' : 'Dismiss'}
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={handleDeleteAlert}
                    style={{
                      padding: '8px 16px',
                      background: deleting ? '#fee2e2' : '#fff',
                      color: '#991b1b',
                      border: '1px solid #fecaca',
                      borderRadius: 7,
                      fontSize: T.small,
                      fontWeight: 700,
                      cursor: deleting ? 'not-allowed' : 'pointer',
                    }}
                    title="Admin: permanently remove this alert (e.g. pipeline noise)"
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {gamingModal && contextWindow && (
            <div
              role="dialog"
              aria-modal="true"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                background: 'rgba(15, 23, 42, 0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                boxSizing: 'border-box',
              }}
              onClick={() => setGamingModal(null)}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e2e8f0',
                  maxWidth: 'min(1100px, 96vw)',
                  width: '100%',
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    padding: '14px 18px',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    flexShrink: 0,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: T.normal, fontWeight: 800, color: '#1e293b' }}>
                      {gamingModal === 'meter' && 'Meter readings'}
                      {gamingModal === 'sessions' && 'Gaming sessions'}
                      {gamingModal === 'combined' && 'Combined sits'}
                    </div>
                    <div style={{ fontSize: T.small, color: C.slate, marginTop: 6 }}>
                      {formatExpandedContextWindowRange(contextWindow.from, contextWindow.to)}
                      <span style={{ color: C.slateL }}>
                        {' '}· ±6 hours from {contextWindow.anchorSource || 'trigger time'}
                      </span>
                    </div>
                    {gamingModalFullPageUrl && (
                      <Link
                        to={gamingModalFullPageUrl}
                        onClick={() => setGamingModal(null)}
                        style={{
                          fontSize: T.small,
                          color: C.pri,
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          marginTop: 8,
                        }}
                      >
                        Open full Gaming Data <ExternalLink size={14} />
                      </Link>
                    )}
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setGamingModal(null)}
                    style={{
                      background: '#f1f5f9',
                      border: 'none',
                      borderRadius: 8,
                      padding: 8,
                      cursor: 'pointer',
                      color: C.slate,
                      lineHeight: 0,
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div style={{ padding: '12px 18px', flex: 1, overflow: 'auto', minHeight: 0 }}>
                  {gamingModalData.loading && (
                    <div style={{ fontSize: T.normal, color: C.slate, padding: 24, textAlign: 'center' }}>Loading…</div>
                  )}
                  {!gamingModalData.loading && !gamingModalData.error && gamingModalData.rows.length > 0 && gamingModalData.dataProvenance === 'api' && (
                    <div
                      style={{
                        fontSize: T.small,
                        color: C.slate,
                        marginBottom: 12,
                        padding: '8px 12px',
                        background: '#f8fafc',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <strong>Data source:</strong> tenant gaming database (API) for this window.
                    </div>
                  )}
                  {!gamingModalData.loading &&
                    !gamingModalData.error &&
                    gamingModalData.dataProvenance === 'alert_payload' &&
                    gamingModalData.rows.length > 0 && (
                    <div
                      style={{
                        fontSize: T.small,
                        color: '#92400e',
                        marginBottom: 12,
                        background: '#fffbeb',
                        border: '1px solid #fcd34d',
                        borderRadius: 8,
                        padding: 10,
                      }}
                    >
                      <strong>Data source: alert payload only.</strong> The gaming API returned no rows for this ±6h window. Values below are
                      reconstructed from <code style={{ fontSize: 11 }}>details</code> stored when this alert was written (trigger / evaluation
                      snapshots), not a fresh pull from venue feeds.
                    </div>
                  )}
                  {gamingModalData.error && (
                    <div style={{ fontSize: T.small, color: C.red, padding: 8 }}>{gamingModalData.error}</div>
                  )}
                  {gamingModalData.hint && !gamingModalData.error && (
                    <div
                      style={{
                        fontSize: T.small,
                        color: '#92400e',
                        background: '#fffbeb',
                        border: '1px solid #fcd34d',
                        borderRadius: 8,
                        padding: 10,
                        marginBottom: 12,
                      }}
                    >
                      {gamingModalData.hint}
                    </div>
                  )}
                  {!gamingModalData.loading && !gamingModalData.error && gamingModal === 'sessions' && (
                    <div style={{ overflowX: 'auto' }}>
                      {alertSessionPk && (
                        <div
                          style={{
                            fontSize: T.small,
                            color: C.slate,
                            marginBottom: 12,
                            padding: '8px 12px',
                            background: '#f0f9ff',
                            borderRadius: 8,
                            border: '1px solid #bae6fd',
                          }}
                        >
                          Raw FactPlayerSession rows (session turnover / stroke). For allocated CashIn use{' '}
                          <strong>Combined sits</strong>.
                          {triggerCombinedHighlight && (
                            <div style={{ marginTop: 4, color: '#0369a1' }}>
                              Highlighted = alert session ({alertSessionPk}).
                            </div>
                          )}
                        </div>
                      )}
                      <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            {[
                              'SessionPK',
                              'MemberID',
                              'EgmPK',
                              'StartTime',
                              'EndTime',
                              'Turnover',
                              'CreditWin',
                              'Venue',
                            ].map((h) => (
                              <th
                                key={h}
                                style={{
                                  textAlign: 'left',
                                  padding: '8px 10px',
                                  borderBottom: '1px solid #e2e8f0',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {gamingModalData.rows.map((r, rowIdx) => {
                            const isTrigger = triggerCombinedHighlight?.index === rowIdx
                            return (
                              <tr
                                key={r.SessionPK || rowIdx}
                                ref={isTrigger ? triggerCombinedRowRef : undefined}
                                style={{
                                  borderBottom: '1px solid #f1f5f9',
                                  background: isTrigger ? '#fef3c7' : undefined,
                                  boxShadow: isTrigger ? 'inset 3px 0 0 #f59e0b' : undefined,
                                  fontWeight: isTrigger ? 700 : undefined,
                                }}
                              >
                                <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>
                                  {isTrigger && (
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        marginRight: 6,
                                        fontSize: 10,
                                        fontWeight: 800,
                                        color: '#b45309',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      Trigger
                                    </span>
                                  )}
                                  {r.SessionPK}
                                </td>
                                <td style={{ padding: '8px 10px' }}>{r.MemberID || '—'}</td>
                                <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{r.EgmPK}</td>
                                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                                  {r.StartTime ? formatSydneyDateTime(r.StartTime) : '—'}
                                </td>
                                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                                  {r.EndTime ? formatSydneyDateTime(r.EndTime) : '—'}
                                </td>
                                <td style={{ padding: '8px 10px' }}>{r.Turnover ?? '—'}</td>
                                <td style={{ padding: '8px 10px' }}>{r.CreditWin ?? '—'}</td>
                                <td style={{ padding: '8px 10px' }}>{r.Venue ?? '—'}</td>
                              </tr>
                            )
                          })}
                          {gamingModalData.rows.length === 0 && (
                            <tr>
                              <td colSpan={8} style={{ padding: 16, color: C.slateL, textAlign: 'center' }}>
                                No gaming sessions with start time in this window.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!gamingModalData.loading && !gamingModalData.error && gamingModal === 'combined' && (
                    <div style={{ overflowX: 'auto' }}>
                      <div
                        style={{
                          fontSize: T.small,
                          color: C.slate,
                          marginBottom: 12,
                          padding: '8px 12px',
                          background: '#f0fdf4',
                          borderRadius: 8,
                          border: '1px solid #bbf7d0',
                        }}
                      >
                        <strong>Combined</strong> = meter intervals allocated onto sessions (Process &amp; Combine).
                        CashIn here is usually what bill-stuffing / cash rules see.
                        {alertSessionPk && (
                          <div style={{ marginTop: 4, color: '#166534', fontFamily: 'monospace', fontSize: 11 }}>
                            Alert session: {alertSessionPk}
                          </div>
                        )}
                      </div>
                      <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            {[
                              'Session start',
                              'SessionPK',
                              'Member',
                              'EgmPK',
                              'Stroke',
                              'Turnover',
                              'CreditWin',
                              'CashIn',
                              'CashlessIn',
                              'Venue',
                            ].map((h) => (
                              <th
                                key={h}
                                style={{
                                  textAlign: 'left',
                                  padding: '8px 10px',
                                  borderBottom: '1px solid #e2e8f0',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {gamingModalData.rows.map((r, rowIdx) => {
                            const isTrigger = triggerCombinedHighlight?.index === rowIdx
                            const isUnc =
                              r.IsUncarded === true ||
                              r.IsUncarded === 't' ||
                              r.MemberID === '__UNCARDED__' ||
                              String(r.SessionPK || '').startsWith('UNC_')
                            const turnover = Number(r.Turnover_alloc ?? r.turnover_alloc ?? 0) || 0
                            const win = Number(r.CreditWin_alloc ?? r.creditwin_alloc ?? 0) || 0
                            const cashInCents =
                              (Number(r.CashIn_alloc ?? r.cashin_alloc ?? 0) || 0) +
                              (Number(r.CashDrop_alloc ?? r.cashdrop_alloc ?? 0) || 0)
                            const cashDollars = r.CashIn_dollars != null ? Number(r.CashIn_dollars) : null
                            const cashless = Number(r.CashlessIn_alloc ?? r.cashlessin_alloc ?? 0) || 0
                            const strokeAlloc = Number(r.Stroke_alloc ?? r.stroke_alloc ?? 0) || 0
                            const strokeEnrich =
                              Number(r.EnrichedMeterStroke ?? r.enrichedmeterstroke ?? r.EnrichedSessionStroke ?? r.enrichedsessionstroke ?? 0) || 0
                            const strokeSession = Number(r.SessionStroke ?? r.sessionstroke ?? 0) || 0
                            // Same precedence as Gaming Data Combined: alloc → enrich → raw session stroke
                            const stroke = strokeAlloc > 0 ? strokeAlloc : strokeEnrich > 0 ? strokeEnrich : strokeSession
                            const start = r.SessionStart || r.FirstInterval || r.sessionstart
                            const fmtAlloc = (cents) => (cents ? formatMeterCents(cents) : '—')
                            return (
                              <tr
                                key={`${r.SessionPK}-${rowIdx}`}
                                ref={isTrigger ? triggerCombinedRowRef : undefined}
                                style={{
                                  borderBottom: '1px solid #f1f5f9',
                                  background: isTrigger ? '#fef3c7' : undefined,
                                  boxShadow: isTrigger ? 'inset 3px 0 0 #f59e0b' : undefined,
                                  fontWeight: isTrigger ? 700 : undefined,
                                }}
                              >
                                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                                  {isTrigger && (
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        marginRight: 6,
                                        fontSize: 10,
                                        fontWeight: 800,
                                        color: '#b45309',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      Trigger
                                    </span>
                                  )}
                                  {start ? formatSydneyDateTime(start) : '—'}
                                </td>
                                <td
                                  style={{
                                    padding: '8px 10px',
                                    fontFamily: 'monospace',
                                    maxWidth: 160,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={r.SessionPK}
                                >
                                  {r.SessionPK}
                                </td>
                                <td style={{ padding: '8px 10px' }}>
                                  {isUnc ? 'Uncarded' : r.MemberID || '—'}
                                </td>
                                <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{r.EgmPK}</td>
                                <td
                                  style={{ padding: '8px 10px' }}
                                  title={
                                    strokeAlloc > 0
                                      ? 'From meter stroke_alloc'
                                      : strokeEnrich > 0
                                        ? 'From session enrich (meter stroke delta / session stroke)'
                                        : strokeSession > 0
                                          ? 'From gaming_sessions.stroke'
                                          : undefined
                                  }
                                >
                                  {stroke > 0 ? Math.round(stroke).toLocaleString() : '—'}
                                </td>
                                <td style={{ padding: '8px 10px' }}>{fmtAlloc(turnover)}</td>
                                <td style={{ padding: '8px 10px' }}>{fmtAlloc(win)}</td>
                                <td style={{ padding: '8px 10px' }}>
                                  {cashDollars != null && Number.isFinite(cashDollars)
                                    ? formatGamingMoney(cashDollars)
                                    : fmtAlloc(cashInCents)}
                                </td>
                                <td style={{ padding: '8px 10px' }}>{fmtAlloc(cashless)}</td>
                                <td style={{ padding: '8px 10px' }}>{r.Venue ?? '—'}</td>
                              </tr>
                            )
                          })}
                          {gamingModalData.rows.length === 0 && (
                            <tr>
                              <td colSpan={10} style={{ padding: 16, color: C.slateL, textAlign: 'center' }}>
                                No combined sits in this window. Run Process &amp; Combine for the trading day.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!gamingModalData.loading && !gamingModalData.error && gamingModal === 'meter' && (
                    <div style={{ overflowX: 'auto' }}>
                      {(meterModalMemberId || triggerMeterHighlight || meterModalEvaluated) && (
                        <div
                          style={{
                            fontSize: T.small,
                            color: C.slate,
                            marginBottom: 12,
                            padding: '8px 12px',
                            background: '#f0f9ff',
                            borderRadius: 8,
                            border: '1px solid #bae6fd',
                          }}
                        >
                          {meterModalEvaluated && (
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ fontWeight: 800, color: '#0c4a6e', marginBottom: 4 }}>
                                {meterModalEvaluated.title}
                              </div>
                              {(meterModalEvaluated.sessionPk || meterModalEvaluated.start) && (
                                <div style={{ fontSize: 11, color: C.slate, marginBottom: 4, fontFamily: 'monospace' }}>
                                  {meterModalEvaluated.sessionPk || 'Session'}
                                  {meterModalEvaluated.start && (
                                    <>
                                      {' · '}
                                      {formatSydneyDateTime(meterModalEvaluated.start)}
                                      {meterModalEvaluated.end
                                        ? ` → ${formatSydneyDateTime(meterModalEvaluated.end)}`
                                        : ''}
                                    </>
                                  )}
                                </div>
                              )}
                              {meterModalEvaluated.moneyLine && (
                                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                                  {meterModalEvaluated.moneyLine}
                                </div>
                              )}
                              {meterModalEvaluated.note && (
                                <div style={{ fontSize: 11, color: '#92400e', lineHeight: 1.4 }}>
                                  {meterModalEvaluated.note}
                                </div>
                              )}
                            </div>
                          )}
                          {meterModalMemberId ? (
                            <>
                              <strong>Member ID:</strong> {meterModalMemberId}
                              <span style={{ color: C.slateL }}>
                                {' '}
                                · meters are EGM-level; member is from this alert
                              </span>
                            </>
                          ) : (
                            <span>
                              <strong>Member ID:</strong> uncarded / not on alert
                            </span>
                          )}
                          {triggerMeterHighlight && (
                            <div style={{ marginTop: 4, color: '#0369a1' }}>
                              Highlighted row ({triggerMeterHighlight.badge || 'Near'}) ={' '}
                              {triggerMeterHighlight.matchKind}. Scroll to see before/after snapshots.
                            </div>
                          )}
                        </div>
                      )}
                      <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            {[
                              'SnapshotTime',
                              'MemberID',
                              'EgmPK',
                              'GamingDate',
                              'Turnover',
                              'CreditWin',
                              'Stroke',
                              'CashIn',
                              'CashlessIn',
                            ].map((h) => (
                              <th
                                key={h}
                                style={{
                                  textAlign: 'left',
                                  padding: '8px 10px',
                                  borderBottom: '1px solid #e2e8f0',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {gamingModalData.rows.map((r, rowIdx) => {
                            const isTrigger = triggerMeterHighlight?.index === rowIdx
                            const badge = triggerMeterHighlight?.badge || 'Near'
                            return (
                              <tr
                                key={`${r.EgmPK}-${r.SnapshotTime}-${rowIdx}`}
                                ref={isTrigger ? triggerMeterRowRef : undefined}
                                title={isTrigger ? `${badge}: ${triggerMeterHighlight.matchKind}` : undefined}
                                style={{
                                  borderBottom: '1px solid #f1f5f9',
                                  background: isTrigger ? '#fef3c7' : undefined,
                                  boxShadow: isTrigger ? 'inset 3px 0 0 #f59e0b' : undefined,
                                  fontWeight: isTrigger ? 700 : undefined,
                                }}
                              >
                                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                                  {isTrigger && (
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        marginRight: 6,
                                        fontSize: 10,
                                        fontWeight: 800,
                                        color: '#b45309',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.3,
                                      }}
                                    >
                                      {badge}
                                    </span>
                                  )}
                                  {r.SnapshotTime ? formatSydneyDateTime(r.SnapshotTime) : '—'}
                                </td>
                                <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>
                                  {r.MemberID || meterModalMemberId || '—'}
                                </td>
                                <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{r.EgmPK}</td>
                                <td style={{ padding: '8px 10px' }}>{r.GamingDate || '—'}</td>
                                <td style={{ padding: '8px 10px' }}>{r.Turnover ?? '—'}</td>
                                <td style={{ padding: '8px 10px' }}>{r.CreditWin ?? '—'}</td>
                                <td style={{ padding: '8px 10px' }}>{r.Stroke ?? '—'}</td>
                                <td style={{ padding: '8px 10px' }}>{r.CashIn ?? '—'}</td>
                                <td style={{ padding: '8px 10px' }}>{r.CashlessIn ?? '—'}</td>
                              </tr>
                            )
                          })}
                          {gamingModalData.rows.length === 0 && (
                            <tr>
                              <td colSpan={9} style={{ padding: 16, color: C.slateL, textAlign: 'center' }}>
                                No meter snapshots in this window.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
